import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  AlignItems,
  OrderItemSort,
  OrderItemTableColumnType,
  PackingSlipBlockType,
  PackingSlipFontWeight,
  PackingSlipSortDirection,
  PageOrientation,
  PageSize,
  QrCodeSize,
} from "./packing-slip-builder";

const baseSectionSchema = z.object({
  sectionPadding: z.object({
    top: z.number(),
    right: z.number(),
    bottom: z.number(),
    left: z.number(),
  }),
  alignSelf: createEnum(AlignItems, { enumName: "AlignItems" }).nullish(),
  blockId: zExt.objectId(),
  sectionColor: z.string().nullish(),
});

const fontSectionSchema = baseSectionSchema.extend({
  fontFamily: z.string().nullish(),
  fontSize: z.number().nullish(),
  textColor: z.string().nullish(),
  fontWeight: createEnum(PackingSlipFontWeight, {
    enumName: "PackingSlipFontWeight",
  }).nullish(),
});

const addressSectionSchema = fontSectionSchema.extend({
  showHeader: z.boolean().nullish(),
  header: z.string().nullish(),
  showName: z.boolean().nullish(),
  boldName: z.boolean().nullish(),
  showCompany: z.boolean().nullish(),
  showAddress: z.boolean().nullish(),
  showPhone: z.boolean().nullish(),
  showEmail: z.boolean().nullish(),
});

const billingAddressSchema = addressSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.BILLING_ADDRESS),
});

const companyAddressSchema = addressSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.COMPANY_ADDRESS),
});

const contactInformationSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.CONTACT_INFORMATION),
  showName: z.boolean(),
  showCompany: z.boolean().nullish(),
  showAddress: z.boolean(),
  showPhone: z.boolean(),
  showEmail: z.boolean(),
});

const noteFromCustomerSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.NOTE_FROM_CUSTOMER),
});

const noteToCustomerSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.NOTE_TO_CUSTOMER),
});

const orderBarcodeSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.ORDER_BARCODE),
  width: z.number().nullish(),
  height: z.number().nullish(),
  useOrderNumber: z.boolean().nullish(),
  removeMarketplaceIdentifier: z.boolean().nullish(), // removes BC-, EBAY-, CS- etc. Will not work within redo
  useCustomerName: z.boolean().nullish(),
  showAnnotation: z.boolean().nullish(),
});

const orderDateSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.ORDER_DATE),
});

const printedDateSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.PRINTED_DATE),
  showPrefix: z.boolean().nullish(),
});

const orderItemsSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.ORDER_ITEMS),
  sort: createEnum(OrderItemSort, { enumName: "OrderItemSort" }),
  customSortField: z.string().nullish(),
  sortDirection: createEnum(PackingSlipSortDirection, {
    enumName: "PackingSlipSortDirection",
  }).nullish(),
  tableColumns: z.array(
    z.object({
      type: createEnum(OrderItemTableColumnType, {
        enumName: "OrderItemTableColumnType",
      }),
      enabled: z.boolean(),
      id: z.string().nullish(),
      title: z.string().nullish(),
      relativeFontSize: z.number().nullish(),
      fontWeight: createEnum(PackingSlipFontWeight, {
        enumName: "PackingSlipFontWeight",
      }).nullish(),
    }),
  ),
  includeMetafields: z.boolean().nullish(),
  rowHeight: z.number().nullish(),
  emphasizeQuantity: z.boolean().nullish(),
  showDescription: z.boolean().nullish(),
  includeHeader: z.boolean().nullish(),
  tableBorderColor: z.string().nullish(),
  tableBorderWidth: z.number().nullish(),
  customAttributes: z.array(z.string()).nullish(),
  showAllCustomAttributes: z.boolean().nullish(),
  splitItemsToSingleQuantity: z.boolean().nullish(),
});

const orderDetailsSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.ORDER_DETAILS),
  showRecipientInformation: z.boolean(),
  showOrderNumber: z.boolean(),
  showOrderDate: z.boolean(),
  showShippingService: z.boolean(),
  stacked: z.boolean().nullish(),
});

const orderNumberSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.ORDER_NUMBER),
});

const packageInformationSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.PACKAGE_INFORMATION),
});

const paymentBreakdownSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.PAYMENT_BREAKDOWN),
});

const paymentMethodSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.PAYMENT_METHOD),
  showSubtotal: z.boolean(),
  showPaymentMethod: z.boolean(),
  showPaymentDate: z.boolean(),
});

const packageWeightSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.PACKAGE_WEIGHT),
});

const shippingAddressSchema = addressSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.SHIPPING_ADDRESS),
});

const requestedShippingMethodSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.REQUESTED_SHIPPING_METHOD),
});

const shippingMethodSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.SHIPPING_METHOD),
});

const trackingNumberSchema = fontSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.TRACKING_NUMBER),
});

const textSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.TEXT),
  textColor: z.string(),
  fontSize: z.number(),
  fontFamily: z.string(),
  linkColor: z.string(),
  text: z.string(),
});

const lineSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.LINE),
  color: z.string(),
});

const spacerSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.SPACER),
  height: z.number(),
});

const imageSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.IMAGE),
  imageUrl: z.string(),
  showCaption: z.boolean(),
  caption: z.string().nullish(),
  altText: z.string().nullish(),
  clickthroughUrl: z.string().nullish(),
  aspectRatio: z.number().nullish(),
  maxHeight: z.number().nullish(),
  resolution: z.number().nullish(),
});

const qrCodeSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.QR_CODE),
  url: z.string(),
  size: createEnum(QrCodeSize, { enumName: "QrCodeSize" }),
  useReturnsPortalUrl: z.boolean(),
});

const logoSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.LOGO),
  maxHeight: z.number().nullish(),
  imageUrl: z.string().nullish(),
});

const headerMarkerSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.HEADER_MARKER),
});

const sectionWithoutColumnSchema = z.discriminatedUnion("type", [
  billingAddressSchema,
  companyAddressSchema,
  contactInformationSchema,
  noteFromCustomerSchema,
  noteToCustomerSchema,
  orderBarcodeSchema,
  orderDateSchema,
  printedDateSchema,
  orderItemsSchema,
  orderDetailsSchema,
  orderNumberSchema,
  packageInformationSchema,
  paymentBreakdownSchema,
  paymentMethodSchema,
  packageWeightSchema,
  shippingAddressSchema,
  requestedShippingMethodSchema,
  shippingMethodSchema,
  trackingNumberSchema,
  textSchema,
  lineSchema,
  spacerSchema,
  imageSchema,
  logoSchema,
  headerMarkerSchema,
  qrCodeSchema,
]);

const columnSchema = baseSectionSchema.extend({
  type: z.literal(PackingSlipBlockType.COLUMN),
  columns: z.array(z.lazy(() => sectionWithoutColumnSchema)),
  columnCount: z.number(),
  gap: z.number(),
});

// Finally, combine all schemas
const sectionSchema = z.discriminatedUnion("type", [
  ...sectionWithoutColumnSchema._def.options,
  columnSchema,
]);

export type PackingSlipSection = z.infer<typeof sectionSchema>;

export const pageLayoutSchema = z.object({
  orientation: createEnum(PageOrientation, { enumName: "PageOrientation" }),
  size: createEnum(PageSize, { enumName: "PageSize" }),
});
export type PackingSlipPageLayout = z.infer<typeof pageLayoutSchema>;

export interface IPackingSlipPageLayout
  extends z.ZodType<PackingSlipPageLayout> {}

export const packingSlipSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  sections: z.array(sectionSchema),
  team: zExt.objectId(),
  updatedAt: z.string(),
  pageLayout: pageLayoutSchema as IPackingSlipPageLayout,
  isLocalPickUpLabel: z.boolean().nullish(),
  combineHalfPageSlips: z.boolean().nullish(),
  deletedAt: z.string().optional(),
});
export const packingSlipSchema: z.ZodType<PackingSlip> = packingSlipSchema_;
export interface PackingSlip extends z.infer<typeof packingSlipSchema_> {}
